import * as _ from 'lodash';
import { Vector3 } from 'three';
import { Load } from './load';

export enum AreaType {
  default = 0,
  barrelHoneycombBase = 1,
  barrelType = 5,
  loadStamp = 9,
  pctExtend = 10,
  pctCorner = 12,
  pctBase = 13,
  pctHelper = 14,
}

export class Area {
  constructor(
    public length: number,
    public width: number,
    public height: number,
    public x: number,
    public y: number,
    public z: number,
    public type?: AreaType,
  ) {}

  public x2(): number {
    return _.round(this.x + this.width, 2);
  }

  public y2(): number {
    return _.round(this.y + this.height, 2);
  }

  public z2(): number {
    return _.round(this.z + this.length, 2);
  }

  public isPartiallyExtendingLeftSide(area: Area): boolean {
    return (
      this.z === area.z2() &&
      ((this.x <= area.x && this.x2() > area.x) || (this.x > area.x && this.x < area.x2())) &&
      this.isSameHeight(area)
    );
  }

  public isPartiallyExtendingRightSide(area: Area): boolean {
    return (
      this.z2() === area.z &&
      ((this.x <= area.x && this.x2() > area.x) || (this.x > area.x && this.x < area.x2())) &&
      this.isSameHeight(area)
    );
  }

  public isPartiallyExtendingTopSide(area: Area): boolean {
    return (
      this.x2() === area.x &&
      ((this.z <= area.z && this.z2() > area.z) || (this.z > area.z && this.z < area.z2())) &&
      this.isSameHeight(area)
    );
  }

  public isPartiallyExtendingBottomSide(area: Area): boolean {
    return (
      this.x === area.x2() &&
      ((this.z <= area.z && this.z2() > area.z) || (this.z > area.z && this.z < area.z2())) &&
      this.isSameHeight(area)
    );
  }

  public isContained(area: Area): boolean {
    return (
      this.x >= area.x &&
      this.x2() <= area.x2() &&
      this.z >= area.z &&
      this.z2() <= area.z2() &&
      this.y >= area.y &&
      this.y2() <= area.y2()
    );
  }

  public isContainsSmallArea(area: Area): boolean {
    return (
      this.x <= area.x &&
      this.x2() >= area.x2() &&
      this.z <= area.z &&
      this.z2() >= area.z2() &&
      this.y <= area.y &&
      this.y2() >= area.y2()
    );
  }

  public isSamePositionAround(area: Area): boolean {
    return (
      (this.x <= area.x && this.x2() > area.x && this.z <= area.z && this.z2() > area.z) ||
      (this.x2() > area.x &&
        this.x2() <= area.x2() &&
        this.z < area.z2() &&
        this.z2() >= area.z2()) ||
      (this.x < area.x2() && this.x >= area.x && this.z >= area.z && this.z < area.z2()) ||
      (this.x < area.x2() && this.x2() >= area.x2() && this.z2() > area.z && this.z2() <= area.z2())
    );
  }

  public isSamePosition(area: Area): boolean {
    return this.x <= area.x && this.x2() >= area.x2() && this.z <= area.z && this.z2() >= area.z2();
  }

  public isLoadPartiallyLeftSide(load: Load): boolean {
    return (
      this.z === load.z2() &&
      ((this.x <= load.x && this.x2() > load.x) || (this.x > load.x && this.x < load.x2()))
    );
  }

  public isLoadPartiallyRightSide(load: Load): boolean {
    return (
      this.z2() === load.z &&
      ((this.x <= load.x && this.x2() > load.x) || (this.x > load.x && this.x < load.x2()))
    );
  }

  public isLoadPartiallyTopSide(load: Load): boolean {
    return (
      this.x2() === load.x &&
      ((this.z <= load.z && this.z2() > load.z) || (this.z > load.z && this.z < load.z2()))
    );
  }

  public isLoadPartiallyBottomSide(load: Load): boolean {
    return (
      this.x === load.x2() &&
      ((this.z <= load.z && this.z2() > load.z) || (this.z > load.z && this.z < load.z2()))
    );
  }

  public isLoadPartiallyLeftSideAfterExtend(load: Load, pctMultiply: number): boolean {
    const leftExtended = this.z - load.length * pctMultiply;
    return (
      leftExtended <= load.z + load.length &&
      this.z2() > load.z + load.length &&
      ((this.y <= load.y && this.y2() > load.y) ||
        (this.y > load.y && this.y < load.y + load.height)) &&
      ((this.x <= load.x && this.x2() > load.x) ||
        (this.x > load.x && this.x < load.x + load.width))
    );
  }

  public isLoadPartiallyRightSideAfterExtend(load: Load, pctMultiply: number): boolean {
    const rightExtended = this.z2() + load.length * pctMultiply;
    return (
      rightExtended >= load.z &&
      this.z < load.z &&
      ((this.y <= load.y && this.y2() > load.y) || (this.y > load.y && this.y < load.y2())) &&
      ((this.x <= load.x && this.x2() > load.x) || (this.x > load.x && this.x < load.x2()))
    );
  }

  public isLoadPartiallyTopSideAfterExtend(load: Load, pctMultiply: number): boolean {
    const topExtended = this.x2() + load.width * pctMultiply;
    return (
      topExtended >= load.x &&
      this.x < load.x &&
      ((this.y <= load.y && this.y2() > load.y) || (this.y > load.y && this.y < load.y2())) &&
      ((this.z <= load.z && this.z2() > load.z) || (this.z > load.z && this.z < load.z2()))
    );
  }

  public isLoadPartiallyBottomSideAfterExtend(load: Load, pctMultiply: number): boolean {
    const bottomExtended = this.x - load.width * pctMultiply;
    return (
      bottomExtended <= load.x + load.width &&
      this.x2() > load.x + load.width &&
      ((this.y <= load.y && this.y2() > load.y) ||
        (this.y > load.y && this.y < load.y + load.height)) &&
      ((this.z <= load.z && this.z2() > load.z) ||
        (this.z > load.z && this.z < load.z + load.length))
    );
  }

  public canFitLoad(load: Load): boolean {
    const isBarrelArea: boolean = this.type === AreaType.barrelType;
    if (isBarrelArea) {
      const barrelDiameter: number = _.round(
        Math.sqrt(Math.pow(this.width, 2) + Math.pow(this.length, 2)),
        2,
      );
      return (
        this.height >= load.height && barrelDiameter >= load.length && barrelDiameter >= load.width
      );
    } else {
      return this.height >= load.height && this.length >= load.length && this.width >= load.width;
    }
  }

  public canFitLoadWithPctSupport(load: Load, pctMultiply: number): boolean {
    return (
      this.height >= load.height &&
      this.length + pctMultiply * load.length >= load.length &&
      this.width + pctMultiply * load.width >= load.width
    );
  }

  public canFitLoadWithPosition(load: Load, position: Vector3): boolean {
    return (
      this.x <= position.x &&
      this.z <= position.z &&
      this.x2() >= position.x + load.width &&
      this.z2() >= position.z + load.length &&
      this.height >= load.height
    );
  }

  public times100(): void {
    this.x = _.round(this.x * 100, 2);
    this.y = _.round(this.y * 100, 2);
    this.z = _.round(this.z * 100, 2);
    this.length = _.round(this.length * 100, 2);
    this.width = _.round(this.width * 100, 2);
    this.height = _.round(this.height * 100, 2);
  }

  private isSameHeight(area: Area): boolean {
    return this.height === area.height;
  }
}
